import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2, Users, Award, CheckCircle, LogOut, Sun, Moon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API_CONFIG } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import AgentSidebar from "../components/AgentSidebar";
import CandidateDetailsModal from "../components/CandidateDetailsModal";
import { enrichCandidateWithName } from "../utils/resumeUtils";
import { useChatHistory } from "../hooks/useChatHistory";

const ResumeFilteringAgent = ({ selectedRole, roles, onRoleSelect, onNavigate }) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [resumesLoaded, setResumesLoaded] = useState(false);
  const [loadedResumes, setLoadedResumes] = useState([]);
  const [jobDescriptionSent, setJobDescriptionSent] = useState(false); // Track if JD was sent
  const [hasMoreResults, setHasMoreResults] = useState(false); // Track if more results available
  const [processingProgress, setProcessingProgress] = useState(null); // Track resume processing progress
  const [currentResumeName, setCurrentResumeName] = useState(''); // Track current resume being processed
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Prompt limit configuration (20 for production)
  const MAX_PROMPTS_PER_CHAT = 20;

  // Debug: Log API configuration on mount
  useEffect(() => {
    console.log("=== API CONFIGURATION ===");
    console.log("RUNTIME_INVOKER_URL:", API_CONFIG.RUNTIME_INVOKER_URL);
    console.log("RESUME_API:", API_CONFIG.RESUME_API);
    console.log("All API_CONFIG:", API_CONFIG);
  }, []);

  // Chat history integration
  const [currentSessionId, setCurrentSessionId] = useState(`session-${Date.now()}`);
  const { chatSessions, saveChatSession, loadChatSession, deleteChatSession, refreshSessions } = useChatHistory(
    user?.email || user?.username || 'anonymous',
    'recruiter'
  );

  // Calculate number of user prompts in current chat
  const userPromptCount = messages.filter(msg => msg.sender === 'user').length;
  const isPromptLimitReached = userPromptCount >= MAX_PROMPTS_PER_CHAT;

  // Rename chat handler
  const handleRenameChat = async (sessionId, newTitle) => {
    try {
      console.log('=== RENAMING CHAT ===');
      console.log('Session ID:', sessionId);
      console.log('New Title:', newTitle);

      // Load the chat to get its messages
      const chatData = await loadChatSession(sessionId);
      console.log('Loaded chat data:', chatData);

      if (!chatData) {
        throw new Error('Failed to load chat data');
      }

      if (!chatData.messages || !Array.isArray(chatData.messages)) {
        throw new Error('Invalid chat data: messages not found or not an array');
      }

      console.log('Messages count:', chatData.messages.length);

      // Save with new title
      await saveChatSession(sessionId, chatData.messages, newTitle);
      console.log('✅ Chat renamed successfully');
    } catch (error) {
      console.error('❌ Failed to rename chat:', error);
      console.error('Error details:', error.message);
      alert(`Failed to rename chat: ${error.message}`);
    }
  };

  // Delete chat handler
  const handleDeleteChat = async (sessionId) => {
    try {
      await deleteChatSession(sessionId);
      console.log('✅ Chat deleted successfully');

      // If the deleted chat is the current one, clear messages
      if (sessionId === currentSessionId) {
        setMessages([]);
        setCurrentSessionId(`session-${Date.now()}`);
      }
    } catch (error) {
      console.error('❌ Failed to delete chat:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  // Share chat as PDF handler
  const handleShareChat = async (chat) => {
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');

      // Load the full chat data
      const chatData = await loadChatSession(chat.sessionId);
      if (!chatData || !chatData.messages) {
        alert('Failed to load chat data');
        return;
      }

      // Create new PDF document
      const doc = new jsPDF();

      // Set up styling
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Add header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 102, 0);
      doc.text(chatData.title, margin, yPosition);
      yPosition += 8;

      // Add metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Messages: ${chatData.messages.length}`, margin, yPosition);
      yPosition += 8;

      // Add separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      // Add messages
      chatData.messages.forEach((msg, idx) => {
        const sender = msg.sender === 'user' ? 'You' : 'Recruiter Agent';
        const senderColor = msg.sender === 'user' ? [0, 102, 204] : [255, 102, 0];

        // Add sender name
        checkNewPage(6);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...senderColor);
        doc.text(sender, margin, yPosition);
        yPosition += 6;

        // Add message text with word wrap
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const messageLines = doc.splitTextToSize(msg.text, maxWidth);

        messageLines.forEach(line => {
          checkNewPage(5);
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });

        yPosition += 2; // Small gap after message

        // Add top candidates if present
        if (msg.topResumes && msg.topResumes.length > 0) {
          checkNewPage(6);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('Top Candidates:', margin, yPosition);
          yPosition += 6;

          msg.topResumes.forEach((resume) => {
            const candidateText = `  ${resume.rank}. ${resume.actualName || resume.name} - Score: ${resume.score}/10`;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(64, 64, 64);

            const candidateLines = doc.splitTextToSize(candidateText, maxWidth);
            candidateLines.forEach(line => {
              checkNewPage(5);
              doc.text(line, margin, yPosition);
              yPosition += 5;
            });
          });
          yPosition += 2;
        }

        // Add separator between messages
        if (idx < chatData.messages.length - 1) {
          checkNewPage(8);
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
        }
      });

      // Save the PDF
      const fileName = `${chatData.title.replace(/[^a-z0-9]/gi, '_')}_chat.pdf`;
      doc.save(fileName);

      console.log('✅ Chat exported as PDF successfully');
    } catch (error) {
      console.error('❌ Failed to export chat:', error);
      alert('Failed to export chat. Please try again.');
    }
  };

  // Load a specific chat session
  const handleLoadChat = async (sessionId) => {
    try {
      console.log('=== LOADING CHAT SESSION ===');
      console.log('Session ID:', sessionId);

      const chatData = await loadChatSession(sessionId);

      console.log('=== CHAT DATA RECEIVED ===');
      console.log('Full chatData:', JSON.stringify(chatData, null, 2));
      console.log('chatData.messages:', chatData?.messages);
      console.log('chatData.role:', chatData?.role);
      console.log('Is array?', Array.isArray(chatData?.messages));

      if (chatData && chatData.messages && Array.isArray(chatData.messages)) {
        console.log('Number of messages:', chatData.messages.length);
        console.log('First message:', chatData.messages[0]);

        // The messages are already in the correct format from DynamoDB
        setMessages(chatData.messages);
        setCurrentSessionId(sessionId);

        // Restore the role if available
        if (chatData.role && onRoleSelect) {
          console.log('🔄 Restoring role:', chatData.role);
          onRoleSelect(chatData.role);
          // Resumes will be loaded automatically by the useEffect that watches selectedRole
        }

        console.log('✅ Chat loaded successfully with', chatData.messages.length, 'messages');
      } else {
        console.warn('❌ No valid messages array found in chat data');
        console.log('chatData structure:', Object.keys(chatData || {}));
      }
    } catch (error) {
      console.error('❌ Failed to load chat:', error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // CRITICAL: Reset jobDescriptionSent when role changes
  // This ensures that when user switches roles (especially after reopening chat),
  // the next request will send the new role to backend
  useEffect(() => {
    console.log("=== ROLE CHANGED ===");
    console.log("New selectedRole:", selectedRole);
    console.log("Resetting jobDescriptionSent to false");
    setJobDescriptionSent(false);
  }, [selectedRole]);

  // Load resumes when role is selected
  useEffect(() => {
    
    if (selectedRole) {
      loadResumesForRole(selectedRole);
    }
  }, [selectedRole]);

  const loadResumesForRole = async (role) => {
    if (!role) return [];

    try {
      console.log("=== LOADING RESUMES FOR ROLE ===", role);
      setResumesLoaded(false);
      const folderName = role.slice(0, -1); // Remove trailing slash

      // Load all pages
      const pageSize = 50;
      let currentPage = 1;
      let hasMore = true;
      let totalLoaded = 0;
      let allResumes = [];

      while (hasMore && currentPage <= 100) {
        console.log(`Loading page ${currentPage}...`);

        const response = await fetch(`${API_CONFIG.RESUME_API}/get-resumes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folder_name: folderName,
            page: currentPage,
            page_size: pageSize
          })
        });

        if (!response.ok) {
          throw new Error("Failed to load resumes");
        }

        const data = await response.json();
        totalLoaded += data.resumes_in_batch;
        if (data.resumes) {
          allResumes = [...allResumes, ...data.resumes];
        }
        console.log(`Page ${currentPage}: Loaded ${data.resumes_in_batch} resumes (${totalLoaded}/${data.total_resumes})`);

        hasMore = data.has_more;
        currentPage++;
      }

      console.log(`✅ All resumes loaded! Total: ${totalLoaded}`);
      setResumesLoaded(true);
      setLoadedResumes(allResumes);
      return allResumes; // Return the loaded resumes
    } catch (error) {
      console.error("❌ Error loading resumes:", error);
      setResumesLoaded(false);
      return [];
    }
  };

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const updateMessageText = (id, newText, isThinking = false, topResumes = null, metrics = null) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, text: newText, isThinking, topResumes, metrics } : m
      )
    );
  };

  const extractTopResumes = (text) => {
    try {
      console.log("=== EXTRACTING RESUMES FROM TEXT ===");
      console.log("Text length:", text.length);

      // PRIORITY 1: Check for __JSON_DATA__ marker (most reliable)
      const jsonMarkerMatch = text.match(/__JSON_DATA__\s*\n([\s\S]*?)\n__END_JSON__/);
      if (jsonMarkerMatch) {
        try {
          const jsonData = JSON.parse(jsonMarkerMatch[1]);
          if (Array.isArray(jsonData) && jsonData.length > 0) {
            console.log("✓✓✓ EXTRACTED FROM JSON MARKER ===", jsonData);
            return jsonData.map(r => ({
              rank: r.rank || 0,
              name: r.name || 'Unknown',
              score: parseFloat(r.score || 0)
            }));
          }
        } catch (e) {
          console.log("Failed to parse JSON marker data:", e);
        }
      }

      // PRIORITY 2: Try to parse if the entire response is JSON
      try {
        const jsonData = JSON.parse(text);
        if (Array.isArray(jsonData)) {
          console.log("Entire response is JSON array:", jsonData);
          const resumes = jsonData.map((r, idx) => ({
            rank: r.rank || r.Rank || idx + 1,
            name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || `Candidate ${idx + 1}`,
            score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0)
          }));
          // Keep original ranking from backend - do NOT re-sort or re-rank
          console.log("=== EXTRACTED FROM JSON ARRAY ===", resumes);
          return resumes;
        } else if (jsonData.candidates || jsonData.resumes || jsonData.results) {
          const arr = jsonData.candidates || jsonData.resumes || jsonData.results;
          console.log("Found nested array:", arr);
          const resumes = arr.map((r, idx) => ({
            rank: r.rank || r.Rank || idx + 1,
            name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || `Candidate ${idx + 1}`,
            score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0)
          }));
          // Keep original ranking from backend - do NOT re-sort or re-rank
          console.log("=== EXTRACTED FROM NESTED JSON ===", resumes);
          return resumes;
        }
      } catch (e) {
        console.log("Not a JSON response, trying text parsing");
      }

      // PRIORITY 3: Try to find JSON array anywhere in the text
      const jsonMatch = text.match(/\[\s*\{[\s\S]*?\}\s*\]/);
      if (jsonMatch) {
        try {
          const resumes = JSON.parse(jsonMatch[0]);
          if (Array.isArray(resumes) && resumes.length > 0) {
            console.log("Found JSON array in text:", resumes);
            return resumes.map((r, idx) => ({
              rank: r.rank || r.Rank || idx + 1,
              name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || `Candidate ${idx + 1}`,
              score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0),
              fileName: r.name || r.Name || r.candidate || r.Candidate || r.resume_name // Store original filename
            }));
          }
        } catch (e) {
          console.log("JSON parse failed, continuing with text extraction");
        }
      }

      // PRIORITY 4: Text-based extraction with comprehensive patterns
      const lines = text.split('\n');
      const resumes = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.length < 5) continue;

        console.log(`Checking line ${i}: "${line}"`);

        // Comprehensive patterns - using permissive character classes to handle
        // filenames with special chars like +, ', @, #, commas, etc.
        // (e.g. "4+ year's AWS_DevOps Resume.pdf")
        const patterns = [
          // "1. Resume_Name Score: 8/10 | Fit: High" - NEW FORMAT
          /^(\d+)\.\s*([^\n*]+?\.pdf)\s+Score:\s*(\d+(?:\.\d+)?)/i,
          // "DineshKagitapu_AWSCloudEngineer_Resume - Score: 9" or "4+ year's AWS_DevOps Resume.pdf - Score: 7"
          /^([^\n*]{3,}?)\s*[-–—:]\s*Score:\s*(\d+(?:\.\d+)?)/i,
          // "1. **Resume_Name** - Score: 8.5/10" or "1. **Resume_Name** - 8.5/10"
          /^(\d+)\.\s*\*\*([^*]+?)\*\*\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "1. Resume_Name - Score: 8.5" or "1. Resume_Name - 8.5"
          /^(\d+)[.)]\s*([^\n*]{3,}?)\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "Rank 1: Resume_Name - 8.5" or "#1: Resume_Name - 8.5"
          /^(?:Rank|#)\s*(\d+)\s*[:.]\s*([^\n*]{3,}?)\s*[-–—:]\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "Resume_Name: 8.5/10" or "Resume_Name: 8.5"
          /^([^\n*:]{3,100}?)\s*[:]\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?$/i,
          // "**Resume_Name**: 8.5/10"
          /^\*\*([^*]+?)\*\*\s*[:]\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "1) Resume_Name (8.5/10)"
          /^(\d+)\)\s*([^\n*]{3,}?)\s*\((\d+(?:.\d+)?)\s*(?:\/\s*10)?\)/i,
          // "Candidate 1: Resume_Name - 8.5"
          /^(?:Candidate|Resume)\s*(\d+)\s*[:.]\s*([^\n*]{3,}?)\s*[-–—:]\s*(\d+(?:.\d+)?)/i,
          // Original person name patterns (keeping existing functionality)
          // "1. **John Doe** - Score: 8.5/10" or "1. **John Doe** - 8.5/10"
          /^(\d+)\.\s*\*\*([A-Z][^*]+?)\*\*\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "1. John Doe - Score: 8.5" or "1. John Doe - 8.5"
          /^(\d+)[.)]\s*([A-Z][A-Za-z\s.'+\-]+?)\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
          // "John Doe: 8.5/10" or "John Doe: 8.5"
          /^([A-Z][A-Za-z\s.'+\-]{2,40}?)\s*[:]\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?$/i,
          // "**John Doe**: 8.5/10"
          /^\*\*([A-Z][^*]+?)\*\*\s*[:]\s*(\d+(?:.\d+)?)\s*(?:\/\s*10)?/i,
        ];

        for (let p = 0; p < patterns.length; p++) {
          const pattern = patterns[p];
          const match = line.match(pattern);

          if (match) {
            console.log(`✓ Pattern ${p} matched! Groups:`, match);

            let rank, name, score;

            // Pattern 0: NEW FORMAT "1. Resume_Name.pdf Score: 8/10 | Fit: High"
            if (p === 0 && match.length === 4) {
              rank = parseInt(match[1]);
              name = match[2].trim();
              score = parseFloat(match[3]);
            }
            // Pattern 1: Direct "Resume_Name - Score: X" format
            else if (p === 1 && match.length === 3) {
              name = match[1].trim();
              score = parseFloat(match[2]);
              rank = resumes.length + 1;
            }
            // Patterns with rank number (2, 3, 7, 8, 9, 10)
            else if (match.length === 4 && (p === 2 || p === 3 || p === 7 || p === 8 || p === 9 || p === 10)) {
              rank = parseInt(match[1]);
              name = match[2].trim();
              score = parseFloat(match[3]);
            }
            // Patterns with rank number (4)
            else if (p === 4 && match.length === 4) {
              rank = parseInt(match[1]);
              name = match[2].trim();
              score = parseFloat(match[3]);
            }
            // Patterns without rank (name comes first) (5, 6, 11, 12)
            else if ((p === 5 || p === 6 || p === 11 || p === 12) && match.length === 3) {
              name = match[1].trim();
              score = parseFloat(match[2]);
              rank = resumes.length + 1;
            }
            else {
              continue;
            }

            // Clean up name
            name = name.replace(/[:\-–—]$/, '').trim();
            name = name.replace(/\s+/g, ' '); // Remove extra spaces

            // Enhanced validation - accept both resume filenames and person names
            const isValidLength = name.length >= 3 && name.length < 150;
            const isValidScore = !isNaN(score) && score >= 0 && score <= 10;

            // Filter out common false positives (section headings, cities, generic terms)
            const invalidNames = [
              'professional experience', 'work experience', 'education', 'skills',
              'summary', 'objective', 'profile', 'contact', 'references',
              'chennai', 'bangalore', 'hyderabad', 'mumbai', 'delhi', 'pune',
              'india', 'email', 'phone', 'mobile', 'address', 'linkedin',
              'github', 'portfolio', 'website', 'personal', 'details',
              'certifications', 'achievements', 'projects', 'internship'
            ];
            const isInvalidName = invalidNames.some(invalid =>
              name.toLowerCase() === invalid || name.toLowerCase().includes(invalid)
            );

            if (name && isValidLength && isValidScore && !isInvalidName) {
              // Check for duplicates
              if (!resumes.some(r => r.name.toLowerCase() === name.toLowerCase())) {
                resumes.push({ rank, name, score });
                console.log(`✓✓ Added: Rank ${rank}, Name: "${name}", Score: ${score}`);
              } else {
                console.log(`× Duplicate name: "${name}"`);
              }
              break;
            } else {
              console.log(`× Validation failed: name="${name}", score=${score}, nameLength=${name.length}, isInvalid=${isInvalidName}`);
            }
          }
        }
      }

      // Sort by score (highest to lowest), then by rank (lowest to highest)
      console.log("=== BEFORE SORTING ===", resumes);
      resumes.sort((a, b) => {
        // Primary sort: score descending (highest first)
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // Secondary sort: rank ascending (lowest first) for same scores
        return a.rank - b.rank;
      });
      console.log("=== AFTER SORTING (by score DESC, then rank ASC) ===", resumes);
      console.log("Total extracted:", resumes.length);

      return resumes.length > 0 ? resumes : null;
    } catch (err) {
      console.error("Error extracting resumes:", err);
      return null;
    }
  };

  const handleCandidateClick = async (resume) => {
    // Enrich candidate with actual name from resume
    const enrichedCandidate = await enrichCandidateWithName(resume, API_CONFIG.RESUME_API_URL, loadedResumes);
    setSelectedCandidate(enrichedCandidate);
  };

  const enrichTopResumesWithNames = async (resumes) => {
    // Enrich all resumes with actual names
    const enrichedResumes = await Promise.all(
      resumes.map(async (resume) => {
        try {
          const enriched = await enrichCandidateWithName(resume, API_CONFIG.RESUME_API_URL, loadedResumes);
          return enriched;
        } catch (error) {
          console.error('Error enriching resume:', resume.name, error);
          return resume; // Return original if enrichment fails
        }
      })
    );
    return enrichedResumes;
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;
    if (!selectedRole) return alert("Please select a role first.");

    // Check prompt limit
    if (isPromptLimitReached) {
      alert(`You've reached the maximum of ${MAX_PROMPTS_PER_CHAT} prompts per chat. Please start a new chat to continue.`);
      return;
    }

    const text = inputValue;
    setInputValue("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '40px';
    }

    const userId = `u-${Date.now()}`;
    const userMessage = { id: userId, text, sender: "user" };
    pushMessage(userMessage);

    const thinkingId = `b-${Date.now()}`;
    const thinkingMessage = {
      id: thinkingId,
      text: "Analyzing resumes...",
      sender: "bot",
      isThinking: true,
    };
    pushMessage(thinkingMessage);

    setIsLoading(true);
    
    try {
      console.log("=== SENDING REQUEST ===");
      console.log("URL:", API_CONFIG.RUNTIME_INVOKER_URL);
      console.log("URL type:", typeof API_CONFIG.RUNTIME_INVOKER_URL);
      console.log("URL is undefined?", API_CONFIG.RUNTIME_INVOKER_URL === undefined);
      console.log("URL is null?", API_CONFIG.RUNTIME_INVOKER_URL === null);
      
      if (!API_CONFIG.RUNTIME_INVOKER_URL) {
        throw new Error("RUNTIME_INVOKER_URL is not configured. Check your .env file.");
      }
      
      // Determine if we need to send JD (define BEFORE using in isFullEvaluation)
      // CRITICAL: Always send role if it's different from the last sent role
      // This ensures role switching works correctly after reopening a chat
      const isFirstRequest = !jobDescriptionSent;
      const isNewEvaluation = text.toLowerCase().includes('all') || text.toLowerCase().includes('top') || text.toLowerCase().includes('score');
      const shouldSendJD = isFirstRequest || isNewEvaluation;
      
      // CRITICAL FIX: Auto-load resumes if not already loaded
      // This ensures resumes are available for generating questions AND accurate progress count
      let resumesToUse = loadedResumes;
      if (selectedRole && loadedResumes.length === 0) {
        console.log("=== AUTO-LOADING RESUMES ===");
        console.log("Selected role:", selectedRole);
        resumesToUse = await loadResumesForRole(selectedRole);
        console.log("=== RESUMES LOADED ===", resumesToUse.length);
      }
      
      // Get the ACTUAL resume count for the selected role from the API
      let actualResumeCount = resumesToUse.length;
      if (selectedRole) {
        try {
          const countResponse = await fetch(`${API_CONFIG.RESUME_API}/get-resume-count`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folder_name: selectedRole.replace(/\/$/, '') })
          });
          if (countResponse.ok) {
            const countData = await countResponse.json();
            actualResumeCount = countData.total_resumes || resumesToUse.length;
            console.log(`[PROGRESS] Actual resume count from API: ${actualResumeCount}`);
          }
        } catch (e) {
          console.log(`[PROGRESS] Failed to get actual count, using loaded: ${resumesToUse.length}`);
        }
      }
      
      // Detect if this is a full evaluation or follow-up query
      const isFullEvaluation = isFirstRequest || isNewEvaluation;
      
      // Start progress simulation based on ACTUAL resume count from API
      const totalResumes = actualResumeCount || 500; // Use actual count from API
      const chunkSize = 25;
      const totalChunks = Math.ceil(totalResumes / chunkSize);
      const timePerChunk = 15; // Increased to 15 seconds per chunk for more conservative estimate
      const estimatedTime = isFullEvaluation ? totalChunks * timePerChunk : 10; // Follow-ups are faster
      
      console.log(`[PROGRESS] Total resumes: ${totalResumes}, Full eval: ${isFullEvaluation}, Est. time: ${estimatedTime}s`);
      
      setProcessingProgress({
        current: 0,
        total: totalResumes,
        percentage: 0,
        estimatedTime: estimatedTime,
        elapsedTime: 0,
        status: isFullEvaluation ? 'Initializing evaluation...' : 'Processing your query...',
        isFullEvaluation: isFullEvaluation,
        currentResume: ''
      });
      
      // Simulate progress updates with resume names
      const startTime = Date.now();
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        // Cap progress at 90% until actual response arrives
        const estimatedProgress = Math.min((elapsed / estimatedTime) * totalResumes, totalResumes * 0.90);
        const percentage = Math.min((estimatedProgress / totalResumes) * 100, 90);
        
        let status = 'Evaluating resumes...';
        let currentResume = '';
        
        if (isFullEvaluation) {
          // Full evaluation status messages
          if (percentage < 20) {
            status = 'Loading resumes from S3...';
          } else if (percentage < 40) {
            status = 'Analyzing candidate qualifications...';
          } else if (percentage < 60) {
            status = 'Scoring technical skills...';
          } else if (percentage < 80) {
            status = 'Evaluating experience levels...';
          } else {
            status = 'Finalizing results...';
          }
          
          // Simulate current resume being processed
          if (resumesToUse && resumesToUse.length > 0 && estimatedProgress > 0) {
            const currentIndex = Math.min(Math.floor(estimatedProgress), resumesToUse.length - 1);
            const resume = resumesToUse[currentIndex];
            if (resume && resume.file_name) {
              // Extract clean name from filename (remove .pdf, underscores, etc.)
              currentResume = resume.file_name
                .replace(/\.pdf$/i, '')
                .replace(/_/g, ' ')
                .replace(/resume/gi, '')
                .trim()
                .split(' ')
                .slice(0, 3) // Take first 3 words
                .join(' ');
            }
          }
        } else {
          // Follow-up query status messages
          if (percentage < 30) status = 'Analyzing your question...';
          else if (percentage < 60) status = 'Retrieving cached evaluations...';
          else status = 'Generating response...';
        }
        
        setProcessingProgress({
          current: Math.floor(estimatedProgress),
          total: totalResumes,
          percentage: Math.floor(percentage),
          estimatedTime: estimatedTime,
          elapsedTime: elapsed,
          status: status,
          isFullEvaluation: isFullEvaluation,
          currentResume: currentResume
        });
      }, 1000); // Update every second
      
      const requestBody = {
        question: text,
        role: shouldSendJD ? selectedRole : "", // Send role when sending JD
        session_id: currentSessionId // CRITICAL: Send session ID for persistence
      };
      
      console.log("Body:", requestBody);
      console.log("Sending JD:", shouldSendJD);
      console.log("Session ID:", currentSessionId);
      console.log("Selected Role:", selectedRole);

      const response = await fetch(API_CONFIG.RUNTIME_INVOKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      console.log("=== RESPONSE STATUS ===", response.status);
      console.log("=== RESPONSE OK ===", response.ok);
      console.log("=== RESPONSE HEADERS ===", [...response.headers.entries()]);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("=== ERROR RESPONSE TEXT ===", errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      // Get response as text first to debug
      const responseText = await response.text();
      console.log("=== RAW RESPONSE TEXT (first 500 chars) ===", responseText.substring(0, 500));
      console.log("=== RAW RESPONSE TEXT LENGTH ===", responseText.length);

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log("=== PARSED JSON SUCCESSFULLY ===");
      } catch (parseError) {
        console.error("=== JSON PARSE ERROR ===", parseError);
        console.error("=== RESPONSE TEXT ===", responseText);
        throw new Error(`Failed to parse response as JSON: ${parseError.message}`);
      }

      console.log("=== RAW DATA ===", data);
      console.log("=== RAW DATA TYPE ===", typeof data);
      console.log("=== RAW DATA KEYS ===", Object.keys(data));

      // Mark that JD has been sent
      if (shouldSendJD) {
        setJobDescriptionSent(true);
      }

      // Parse the Lambda response
      let final = "";
      let responseMetrics = null;

      // Check if this is a Lambda Function URL response (direct) or API Gateway response (wrapped in body)
      let bodyData = data;

      if (data.body) {
        // API Gateway format - body is a JSON string
        console.log("Found data.body (API Gateway format), parsing...");
        bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      } else if (data.response || data.result) {
        // Function URL format - response is direct
        console.log("Direct response (Function URL format)");
        bodyData = data;
      }

      console.log("=== BODY DATA ===", bodyData);
      console.log("=== BODY DATA.RESULT ===", bodyData.result);

      // Extract metrics if available (check multiple locations)
      if (bodyData.metrics) {
        responseMetrics = bodyData.metrics;
        console.log("=== EXTRACTED METRICS FROM BODYDATA.METRICS ===", responseMetrics);
      } else if (bodyData.result?.metrics) {
        responseMetrics = bodyData.result.metrics;
        console.log("=== EXTRACTED METRICS FROM BODYDATA.RESULT.METRICS ===", responseMetrics);
      } else if (bodyData.result?.result?.metrics) {
        responseMetrics = bodyData.result.result.metrics;
        console.log("=== EXTRACTED METRICS FROM BODYDATA.RESULT.RESULT.METRICS ===", responseMetrics);
      }

      // Check for "load more" availability
      const finalText = bodyData.content?.[0]?.text || bodyData.response || '';
      if (finalText && typeof finalText === 'string') {
        const hasLoadMore = finalText.includes('more candidates available') || 
                           finalText.includes('load more') ||
                           finalText.includes('show more');
        setHasMoreResults(hasLoadMore);
        console.log("=== HAS MORE RESULTS ===", hasLoadMore);
      }

      // Check multiple possible locations for response text
      if (bodyData.content?.[0]?.text) {
        // Direct content format from Lambda: {content: [{type: "text", text: "..."}]}
        final = bodyData.content[0].text;
        console.log("Extracted from bodyData.content[0].text (Lambda direct format)");
      } else if (bodyData.response) {
        // New format with 'response' field (our updated Lambda)
        final = bodyData.response;
        console.log("Extracted from bodyData.response (new format)");
      } else if (bodyData.result?.content?.[0]?.text) {
        final = bodyData.result.content[0].text;
        console.log("Extracted from bodyData.result.content[0].text");
      } else if (bodyData.result?.result?.content?.[0]?.text) {
        final = bodyData.result.result.content[0].text;
        console.log("Extracted from bodyData.result.result.content[0].text");
      } else if (typeof bodyData.result === 'string') {
        final = bodyData.result;
        console.log("Extracted from bodyData.result as string");
      } else if (bodyData.result && typeof bodyData.result === 'object') {
        // Try to extract text from result object with various formats
        const result = bodyData.result;
        if (result.content && Array.isArray(result.content)) {
          // Handle content array format: {"result": {"content": [{"text": "..."}]}}
          const textContent = result.content
            .filter(item => item.text)
            .map(item => item.text)
            .join('\n');
          if (textContent) {
            final = textContent;
            console.log("Extracted from result.content array");
          }
        } else if (result.text) {
          final = result.text;
          console.log("Extracted from result.text");
        } else if (result.message) {
          final = result.message;
          console.log("Extracted from result.message");
        }

        // Only stringify as last resort
        if (!final) {
          console.log("Result exists but format unknown, stringifying");
          final = JSON.stringify(result, null, 2);
        }
      }

      if (!final) {
        console.log("Trying fallback extraction...");
        // Be careful not to set entire objects as the final text
        if (data.content?.[0]?.text) {
          final = data.content[0].text;
        } else if (data.reply) {
          final = data.reply;
        } else if (data.message) {
          final = data.message;
        } else if (typeof data.result === 'string') {
          final = data.result;
        } else if (data.result?.content?.[0]?.text) {
          final = data.result.content[0].text;
        } else {
          final = "No response received.";
        }
      }

      console.log("=== FINAL TEXT (before cleanup) ===", final);

      // Clean up the text - remove escaped characters, code blocks, and JSON wrapper artifacts
      if (typeof final === 'string') {
        final = final.replace(/\\n/g, "\n");
        final = final.replace(/\\"/g, '"');
        final = final.replace(/```json\n?|\n?```/g, '').trim();

        // AGGRESSIVE JSON CLEANUP - Handle full JSON response that leaked into text
        // If text starts with JSON structure, extract the actual text content
        if (final.includes('{"success"') || final.includes('"result"') || final.includes('"content"')) {
          // Try to extract just the text part from: {"success": true, "result": {..., "content": [{"text": "ACTUAL CONTENT"}]}}
          const textMatch = final.match(/"text"\s*:\s*"([\s\S]*?)(?:"\s*\}\s*\]\s*\}\s*\}|\"\s*$)/);
          if (textMatch && textMatch[1]) {
            final = textMatch[1];
            // Unescape any escaped characters in extracted text
            final = final.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          } else {
            // Try alternative: just remove the JSON prefix up to first actual content
            final = final.replace(/^\s*\{"success"\s*:\s*true\s*,\s*"result"\s*:\s*\{[^"]*"content"\s*:\s*\[\s*\{\s*"text"\s*:\s*"/i, '');
            // Remove trailing JSON artifacts
            final = final.replace(/"\s*\}\s*\]\s*\}\s*\}\s*$/g, '');
          }
        }

        // Remove "Final List" section with JSON array (redundant with Top Candidates UI)
        final = final.replace(/\n*\*?\*?Final List\*?\*?\s*\n*\[[\s\S]*?\]\s*["']?\}?\]?\}?\}?/gi, '');

        // Remove JSON_DATA marker section (used for frontend parsing only)
        final = final.replace(/__JSON_DATA__\s*\n[\s\S]*?\n__END_JSON__\s*/g, '');
        final = final.replace(/JSON_DATA\s*\[[\s\S]*?\]\s*END_JSON/g, '');

        // Clean up any remaining trailing JSON artifacts
        final = final.replace(/["']\}?\]?\}?\}\s*$/g, '').trim();
        final = final.replace(/\]\s*\}\s*\}\s*$/g, '').trim();

        // Remove any remaining JSON-like prefixes
        if (final.startsWith('{"') || final.startsWith("{")) {
          try {
            const parsed = JSON.parse(final);
            if (parsed.text) final = parsed.text;
            else if (parsed.content?.[0]?.text) final = parsed.content[0].text;
            else if (parsed.result?.content?.[0]?.text) final = parsed.result.content[0].text;
          } catch (e) {
            // Not valid JSON, keep as is but try one more extraction
            const match = final.match(/"text"\s*:\s*"([^"]+)"/);
            if (match) final = match[1];
          }
        }

        // Final cleanup - remove any stray JSON brackets/braces at end
        final = final.replace(/[\}\]\s]+$/g, '').trim();

        // Ensure proper markdown line breaks
        final = final.replace(/([^\n])\n([^\n])/g, '$1  \n$2');

      } else if (typeof final === 'object') {
        // Extract text from object instead of stringifying
        if (final.text) final = final.text;
        else if (final.content?.[0]?.text) final = final.content[0].text;
        else final = JSON.stringify(final, null, 2);
      }

      console.log("=== FINAL TEXT (after cleanup) ===", final);
      console.log("=== FINAL TEXT TYPE ===", typeof final);
      console.log("=== FINAL TEXT LENGTH ===", final.length);

      // Extract metrics from special marker format: __METRICS__:{...}
      if (!responseMetrics && typeof final === 'string') {
        const markerIndex = final.indexOf('__METRICS__:');
        if (markerIndex !== -1) {
          try {
            // Extract everything after __METRICS__:
            const metricsString = final.substring(markerIndex + 12).trim();
            // Try to parse the JSON
            responseMetrics = JSON.parse(metricsString);
            console.log("=== EXTRACTED METRICS FROM MARKER ===", responseMetrics);
            // Remove the metrics marker from the text
            final = final.substring(0, markerIndex).trim();
            console.log("=== TEXT AFTER MARKER REMOVAL ===", final.slice(-100));
          } catch (e) {
            console.log("=== FAILED TO PARSE METRICS FROM MARKER ===", e);
            // Try with regex as fallback
            const metricsMatch = final.match(/__METRICS__:(\{[^}]+\})/);
            if (metricsMatch) {
              try {
                responseMetrics = JSON.parse(metricsMatch[1]);
                final = final.replace(/__METRICS__:\{[^}]+\}/g, '').trim();
              } catch (e2) {
                console.log("=== REGEX FALLBACK ALSO FAILED ===", e2);
              }
            }
          }
        }
      }

      // Extract metrics from METRICS:{...} format - SIMPLE AND ROBUST
      if (!responseMetrics && typeof final === 'string') {
        console.log("=== CHECKING FOR METRICS IN TEXT ===");
        console.log("=== First 300 chars of final:", final.substring(0, 300));

        // Simple approach: find METRICS: and extract everything until the closing brace
        const metricsStartIndex = final.indexOf('METRICS:');
        if (metricsStartIndex !== -1) {
          console.log("=== FOUND METRICS: at index", metricsStartIndex);

          // Find the JSON object after METRICS:
          const afterMetrics = final.substring(metricsStartIndex + 8); // Skip "METRICS:"
          const jsonStart = afterMetrics.indexOf('{');

          if (jsonStart !== -1) {
            // Find matching closing brace
            let braceCount = 0;
            let jsonEnd = -1;
            for (let i = jsonStart; i < afterMetrics.length; i++) {
              if (afterMetrics[i] === '{') braceCount++;
              if (afterMetrics[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i + 1;
                  break;
                }
              }
            }

            if (jsonEnd !== -1) {
              const jsonString = afterMetrics.substring(jsonStart, jsonEnd);
              console.log("=== EXTRACTED JSON STRING:", jsonString);

              try {
                const parsedMetrics = JSON.parse(jsonString);
                responseMetrics = {
                  inputTokens: parsedMetrics.inputTokens || parsedMetrics.input_tokens || 0,
                  outputTokens: parsedMetrics.outputTokens || parsedMetrics.output_tokens || 0,
                  totalTokens: parsedMetrics.totalTokens || parsedMetrics.total_tokens || 0,
                  executionTimeMs: parsedMetrics.executionTimeMs || parsedMetrics.execution_time_ms || parsedMetrics.latencyMs || 0
                };
                console.log("=== SUCCESSFULLY PARSED METRICS ===", responseMetrics);

                // Remove the METRICS line from text
                const fullMetricsText = 'METRICS:' + afterMetrics.substring(0, jsonEnd);
                final = final.replace(fullMetricsText, '').trim();
                console.log("=== TEXT AFTER REMOVING METRICS (first 200):", final.substring(0, 200));
              } catch (e) {
                console.log("=== FAILED TO PARSE METRICS JSON:", e.message);
              }
            } else {
              console.log("=== COULD NOT FIND CLOSING BRACE FOR METRICS JSON");
            }
          } else {
            console.log("=== NO JSON START BRACE FOUND AFTER METRICS:");
          }
        } else {
          console.log("=== NO METRICS: FOUND IN TEXT");
        }
      }

      // Additional cleanup: Remove any remaining METRICS: lines that weren't caught
      if (typeof final === 'string') {
        // Very aggressive cleanup - remove any line containing METRICS
        const lines = final.split('\n');
        const cleanedLines = lines.filter(line => !line.trim().startsWith('METRICS:') && !line.trim().startsWith('METRICS'));
        final = cleanedLines.join('\n').trim();

        // Also try direct removal patterns
        final = final.replace(/METRICS:\s*\{[^}]*\}/gi, '').trim();
        final = final.replace(/^METRICS.*$/gim, '').trim();

        // Clean up multiple newlines
        final = final.replace(/\n{3,}/g, '\n\n').trim();
      }

      // If we still don't have metrics, try to extract from text as fallback
      if (!responseMetrics && typeof final === 'string') {
        console.log("=== ATTEMPTING TOKEN EXTRACTION FROM TEXT (FALLBACK) ===");
        const tokenMatch = final.match(/Input:\s*([\d,]+)\s*tokens\s*\n*Output:\s*([\d,]+)\s*tokens\s*\n*Total:\s*([\d,]+)\s*tokens\s*\n*([\d.]+)ms/i);
        console.log("Token regex match result:", tokenMatch);

        if (tokenMatch) {
          responseMetrics = {
            inputTokens: parseInt(tokenMatch[1].replace(/,/g, '')),
            outputTokens: parseInt(tokenMatch[2].replace(/,/g, '')),
            totalTokens: parseInt(tokenMatch[3].replace(/,/g, '')),
            executionTimeMs: parseFloat(tokenMatch[4])
          };
          console.log("=== EXTRACTED METRICS FROM TEXT (FALLBACK) ===", responseMetrics);

          // Remove token usage from display text
          final = final.replace(/\s*Input:\s*[\d,]+\s*tokens\s*\n*Output:\s*[\d,]+\s*tokens\s*\n*Total:\s*[\d,]+\s*tokens\s*\n*[\d.]+ms\s*/gi, '');
          final = final.trim();
          console.log("=== TEXT AFTER TOKEN REMOVAL ===", final.slice(-100));
        } else {
          console.log("=== NO TOKEN MATCH FOUND IN TEXT ===");
        }
      } else if (responseMetrics) {
        console.log("=== METRICS ALREADY AVAILABLE FROM OBJECT ===");
        console.log("responseMetrics:", responseMetrics);
      }

      // Extract top resumes from the response
      const topResumes = extractTopResumes(final);
      console.log("=== EXTRACTED TOP RESUMES ===", topResumes);

      // Enrich resumes with actual names from resume content
      let enrichedTopResumes = topResumes;
      if (topResumes && topResumes.length > 0) {
        try {
          enrichedTopResumes = await enrichTopResumesWithNames(topResumes);
          console.log("=== ENRICHED TOP RESUMES ===", enrichedTopResumes);
        } catch (error) {
          console.error("Error enriching resumes:", error);
          // Use original if enrichment fails
          enrichedTopResumes = topResumes;
        }
      }

      // If extraction failed, try to log the text structure
      if (!topResumes || topResumes.length === 0) {
        console.log("=== EXTRACTION FAILED - FIRST 500 CHARS ===");
        console.log(final.substring(0, 500));
        console.log("=== SHOWING ALL LINES ===");
        final.split('\n').forEach((line, idx) => {
          if (line.trim()) console.log(`Line ${idx}: "${line}"`);
        });
      }

      // FINAL CLEANUP: Remove METRICS text right before displaying
      if (typeof final === 'string') {
        console.log("=== FINAL TEXT BEFORE METRICS REMOVAL ===", final.substring(0, 300));

        // Line-by-line cleanup - most reliable method
        const lines = final.split('\n');
        const cleanedLines = lines.filter(line => {
          const trimmed = line.trim();
          // Remove any line that starts with METRICS
          if (trimmed.startsWith('METRICS:') || trimmed.startsWith('METRICS')) {
            console.log("=== REMOVING LINE:", trimmed.substring(0, 100));
            return false;
          }
          return true;
        });
        final = cleanedLines.join('\n');

        // Also apply regex patterns as backup
        final = final.replace(/METRICS:\s*\{[^}]*\}/gi, '');
        final = final.replace(/METRICS:\s*\{[\s\S]*?\}/gi, '');
        final = final.replace(/^METRICS.*$/gim, '');

        // Clean up multiple newlines and trim
        final = final.replace(/\n{3,}/g, '\n\n').trim();

        console.log("=== FINAL TEXT AFTER METRICS REMOVAL ===", final.substring(0, 200));
      }

      // Complete progress to 100% when response is received
      if (processingProgress) {
        setProcessingProgress({
          ...processingProgress,
          current: processingProgress.total,
          percentage: 100,
          status: 'Complete!'
        });
        
        // Brief delay to show 100% before clearing
        setTimeout(() => {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        }, 500);
      }

      updateMessageText(thinkingId, final, false, enrichedTopResumes, responseMetrics);

      // Auto-save chat history after bot responds
      try {
        // Generate a meaningful title from the user message
        const chatTitle = text.substring(0, 60) + (text.length > 60 ? '...' : '');

        // Build the complete messages array manually (since state updates are async)
        // Start with existing messages, add user message and bot response
        const finalBotMessage = {
          id: thinkingId,
          text: final,
          sender: 'bot',
          isThinking: false,
          topResumes: enrichedTopResumes,
          metrics: responseMetrics
        };

        // Construct the complete conversation: existing messages + user message + bot response
        const completeMessages = [...messages, userMessage, finalBotMessage];

        console.log('=== SAVING CHAT HISTORY ===');
        console.log('Session ID:', currentSessionId);
        console.log('Messages count:', completeMessages.length);
        console.log('User message:', userMessage);
        console.log('Bot message:', finalBotMessage);
        console.log('Title:', chatTitle);
        console.log('Role:', selectedRole);

        await saveChatSession(currentSessionId, completeMessages, chatTitle, selectedRole);

        console.log('✅ Chat history saved with title:', chatTitle);
      } catch (saveError) {
        console.error('Failed to save chat history:', saveError);
      }
    } catch (err) {
      console.error("=== ERROR ===", err);
      console.error("=== ERROR NAME ===", err.name);
      console.error("=== ERROR MESSAGE ===", err.message);
      console.error("=== ERROR STACK ===", err.stack);
      updateMessageText(
        thinkingId,
        `⚠️ Error: ${err.message}\n\nPlease check:\n1. AWS credentials are valid\n2. Lambda is deployed\n3. CORS is enabled\n4. Network connection is stable`,
        false,
        null,
        null
      );
    } finally {
      setIsLoading(false);
      
      // Clear progress interval and reset progress
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setProcessingProgress(null);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputValue("");
    setSelectedCandidate(null);
    setJobDescriptionSent(false); // Reset JD flag
    setHasMoreResults(false); // Reset load more flag
    setCurrentSessionId(`session-${Date.now()}`); // Create new session ID
    onRoleSelect(''); // Reset role selection to empty
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }
  };

  // Handler for "Load More" button
  const handleLoadMore = () => {
    setInputValue("load more");
    // Trigger send immediately
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  return (
    <div className="flex w-full h-full overflow-hidden">
      {/* SIDEBAR */}
      <AgentSidebar
        currentAgent="recruiter"
        onNavigate={onNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onNewChat={handleNewChat}
        chatHistory={chatSessions}
        onLoadChat={handleLoadChat}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
        onShareChat={handleShareChat}
      />

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden transition-all duration-500 ease-in-out">
        {/* Theme Toggle - Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => toggleTheme(e)}
            className="theme-toggle-glow p-2 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:scale-110 active:scale-95 hover:rotate-12"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            <div className="relative">
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500 transition-all duration-500 rotate-0" key="sun" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 transition-all duration-500 rotate-0" key="moon" />
              )}
            </div>
          </button>
        </div>

        {/* Wrapper for centering when empty */}
        <div className={`flex-1 flex flex-col ${messages.length === 0 ? 'justify-center' : ''} overflow-hidden transition-opacity duration-500`}>
          {/* CHAT AREA */}
          <div className={`${messages.length === 0 ? '' : 'flex-1 overflow-y-auto'} p-3 sm:p-4 md:p-6`}>
            {messages.length === 0 ? (
              // BEAUTIFUL EMPTY STATE - Centered content
              <div className="text-center max-w-xl mx-auto opacity-90 px-4">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <img
                    src="/CI-logo.png"
                    alt="CI Logo"
                    className="h-12 w-auto sm:h-14 md:h-16"
                  />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white">
                    Recruiter Agent
                  </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg mb-8">
                  Select a role and ask a hiring question to begin.
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto w-full space-y-4 sm:space-y-6">
                {messages.map((msg) => {
                  const isUser = msg.sender === "user";

                  return (
                    <div
                      key={msg.id}
                      className={`flex items-start gap-2 sm:gap-4 ${isUser ? "justify-end" : "justify-start"
                        }`}
                    >
                      {/* Avatar */}
                      {!isUser && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-orange-600 flex items-center justify-center shadow-md flex-shrink-0">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                        </div>
                      )}

                      {/* MESSAGE CONTENT */}
                      <div
                        className={`text-sm sm:text-[15px] leading-relaxed font-['system-ui',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      ${isUser
                            ? "inline-block bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-3xl max-w-[85%] sm:max-w-xl md:max-w-2xl break-words whitespace-pre-wrap"
                            : msg.isThinking 
                              ? "w-full max-w-[85%] sm:max-w-xl md:max-w-2xl text-gray-800 dark:text-gray-200"
                              : "max-w-[85%] sm:max-w-xl md:max-w-2xl text-gray-800 dark:text-gray-200"
                          }`}
                      >
                        {msg.isThinking ? (
                          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 w-full min-w-0">
                            {processingProgress ? (
                              <div className="space-y-4 w-full min-w-0">
                                {/* Header */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                                    <span className="text-gray-900 dark:text-white font-semibold text-lg">
                                      {processingProgress.isFullEvaluation ? 'Processing Resumes' : 'Analyzing Query'}
                                    </span>
                                  </div>
                                </div>
                                
                                {processingProgress.isFullEvaluation ? (
                                  // Full evaluation: Show detailed progress with resume count
                                  <>
                                    {/* Current Resume Being Processed - FIXED SIZE */}
                                    {processingProgress.currentResume && (
                                      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-[72px] w-full min-w-0">
                                        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-orange-600 text-white flex-shrink-0">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                        </div>
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Currently Processing</p>
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate animate-pulse" 
                                             title={processingProgress.currentResume}>
                                            {processingProgress.currentResume}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Progress Bar */}
                                    <div className="space-y-2">
                                      <div className="flex justify-between items-center text-sm text-gray-700 dark:text-gray-300">
                                        <span className="font-medium">{processingProgress.current} of {processingProgress.total} resumes</span>
                                        <span className="font-semibold text-orange-600 ml-4">{processingProgress.percentage}%</span>
                                      </div>
                                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden shadow-inner">
                                        <div 
                                          className="bg-gradient-to-r from-orange-500 to-orange-600 h-full rounded-full transition-all duration-500 ease-out shadow-md relative overflow-hidden"
                                          style={{ width: `${processingProgress.percentage}%` }}
                                        >
                                          {/* Animated shimmer effect */}
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Status and Time */}
                                    <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                        <div className="w-2 h-2 rounded-full animate-pulse bg-orange-600"></div>
                                        <span className="font-medium">{processingProgress.status}</span>
                                      </div>
                                      <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                                        <span>Elapsed: {Math.floor(processingProgress.elapsedTime / 60)}m {processingProgress.elapsedTime % 60}s</span>
                                        <span className="ml-4">Est. Total: {Math.floor(processingProgress.estimatedTime / 60)}m {processingProgress.estimatedTime % 60}s</span>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  // Follow-up query: Show simplified, elegant progress
                                  <>
                                    <div className="space-y-3">
                                      {/* Simplified status */}
                                      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                                        <span className="font-medium">{processingProgress.status}</span>
                                      </div>
                                      
                                      {/* Elegant animated dots */}
                                      <div className="flex justify-center gap-2 py-2">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                      </div>
                                      
                                      {/* Context info */}
                                      <div className="text-xs text-center text-gray-600 dark:text-gray-400 pt-2 border-t border-orange-200 dark:border-gray-600">
                                        Working with {processingProgress.total} resumes from this role
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                                <span className="text-gray-700 dark:text-gray-300 font-medium">Initializing...</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className={`prose prose-gray dark:prose-invert max-w-none ${isUser ? 'text-gray-900 dark:text-gray-100' : 'text-gray-800 dark:text-gray-200'}`}>
                              <ReactMarkdown
                                components={{
                                  // Headers with emoji support
                                  h1: ({ node, ...props }) => <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-4 mb-2 flex items-center gap-2" {...props} />,
                                  h2: ({ node, ...props }) => <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-4 mb-2 flex items-center gap-2" {...props} />,
                                  h3: ({ node, ...props }) => <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-3 mb-1.5" {...props} />,
                                  h4: ({ node, ...props }) => <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-2 mb-1" {...props} />,
                                  // Paragraphs
                                  p: ({ node, ...props }) => <p className="mb-3 leading-relaxed text-gray-800 dark:text-gray-200" {...props} />,
                                  // Lists
                                  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 space-y-1.5 ml-1 text-gray-800 dark:text-gray-200" {...props} />,
                                  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 space-y-1.5 ml-1 text-gray-800 dark:text-gray-200" {...props} />,
                                  li: ({ node, ...props }) => <li className="leading-relaxed text-gray-800 dark:text-gray-200" {...props} />,
                                  // Bold and emphasis
                                  strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900 dark:text-white" {...props} />,
                                  em: ({ node, ...props }) => <em className="italic text-orange-600 dark:text-orange-400" {...props} />,
                                  // Code blocks
                                  code: ({ node, inline, ...props }) =>
                                    inline
                                      ? <code className="bg-gray-100 dark:bg-gray-800 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
                                      : <code className="block bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto my-3" {...props} />,
                                  pre: ({ node, ...props }) => <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto my-3" {...props} />,
                                  // Links
                                  a: ({ node, ...props }) => <a className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline" {...props} />,
                                  // Blockquotes
                                  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-orange-500 pl-4 italic text-gray-600 dark:text-gray-400 my-3" {...props} />,
                                  // Horizontal rule
                                  hr: ({ node, ...props }) => <hr className="border-gray-200 dark:border-gray-700 my-4" {...props} />,
                                }}
                              >
                                {/* Clean METRICS from text at render time as last resort */}
                                {(msg.text || '').split('\n').filter(line => !line.trim().startsWith('METRICS:')).join('\n').replace(/METRICS:\s*\{[^}]*\}/gi, '').trim()}
                              </ReactMarkdown>
                            </div>

                            {/* Top Resumes List */}
                            {msg.topResumes && msg.topResumes.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2 mb-4">
                                  <Award className="w-5 h-5 text-orange-600" />
                                  <h3 className="font-semibold text-gray-900 dark:text-white text-base">Top Candidates</h3>
                                </div>
                                <div className="space-y-2.5">
                                  {/* Display candidates in the order received from backend (already sorted) */}
                                  {msg.topResumes.map((resume, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => handleCandidateClick(resume)}
                                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-orange-300 dark:hover:border-orange-500 transition-all group cursor-pointer"
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                          {resume.rank}
                                        </div>
                                        <div className="flex-1">
                                          <p className="font-semibold text-gray-900 dark:text-white group-hover:text-orange-600 transition text-[15px]">
                                            {resume.fileName || resume.name}
                                          </p>
                                          {resume.actualName && (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Name: {resume.actualName}</p>
                                          )}
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Click to view details & generate questions</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-2xl font-bold text-orange-600">
                                            {resume.score}
                                          </span>
                                          <span className="text-gray-500 dark:text-gray-400 text-xs font-medium">/10</span>
                                        </div>
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Metrics Footer */}
                            {msg.metrics && !isUser && (
                              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Input: {msg.metrics.inputTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Output: {msg.metrics.outputTokens?.toLocaleString() || 0} tokens</span>
                                  </div>
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Total: {msg.metrics.totalTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Elapsed Time: {msg.metrics.executionTimeMs ? `${msg.metrics.executionTimeMs}ms` : msg.metrics.latencyMs ? `${msg.metrics.latencyMs}ms` : 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* USER AVATAR */}
                      {isUser && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg flex-shrink-0">
                          <span className="text-white font-bold text-sm sm:text-base md:text-lg">U</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* LOAD MORE BUTTON - Show when there are more results */}
          {hasMoreResults && !isLoading && messages.length > 0 && (
            <div className="w-full flex justify-center px-4 pb-4">
              <button
                onClick={handleLoadMore}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <span>Load More Results</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}

          {/* FLOATING INPUT BAR - Centered when empty, bottom when chat active */}
          <div className={`w-full flex flex-col items-center px-2 sm:px-0 transition-all duration-300 ${messages.length === 0 ? 'pb-[25vh]' : 'py-3 sm:py-4 md:py-6'}`}>
            {/* Prompt limit warning */}
            {isPromptLimitReached && (
              <div className="w-full sm:w-[90%] max-w-2xl mb-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg px-4 py-2 text-center">
                <p className="text-sm text-orange-800 dark:text-orange-300">
                  <span className="font-semibold">Prompt limit reached!</span> You've used {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts.
                  <button
                    onClick={handleNewChat}
                    className="ml-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline font-semibold"
                  >
                    Start a new chat
                  </button>
                </p>
              </div>
            )}

            {/* Prompt counter (show when not at limit) */}
            {!isPromptLimitReached && userPromptCount > 0 && (
              <div className="w-full sm:w-[90%] max-w-2xl mb-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts used
                </p>
              </div>
            )}

            <div className="w-full sm:w-[90%] max-w-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg flex flex-wrap sm:flex-nowrap items-end gap-2">
              {/* Input */}
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                disabled={isPromptLimitReached}
                placeholder={
                  isPromptLimitReached
                    ? "Prompt limit reached. Start a new chat to continue..."
                    : selectedRole
                      ? "Ask something about the resumes..."
                      : "Select a role to start..."
                }
                rows={1}
                style={{
                  maxHeight: '288px', // 12 lines * 24px line height
                  minHeight: '40px',
                  resize: 'none',
                  overflow: 'auto',
                  paddingTop: '8px',
                  paddingBottom: '8px'
                }}
                onInput={(e) => {
                  const lineHeight = 24;
                  const maxLines = 12;
                  const padding = 16; // top + bottom padding
                  const maxHeight = (lineHeight * maxLines) + padding;

                  e.target.style.height = 'auto';
                  const newHeight = Math.min(e.target.scrollHeight, maxHeight);
                  e.target.style.height = newHeight + 'px';
                }}
                className={`flex-1 bg-transparent outline-none text-gray-800 dark:text-gray-200 text-sm sm:text-base ml-2 leading-6 placeholder:text-gray-400 dark:placeholder:text-gray-500 ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
              />

              {/* RIGHT ACTIONS */}
              <div className="flex items-center gap-2">
                {/* ROLE DROPDOWN */}
                <select
                  value={selectedRole || ""}
                  onChange={(e) => onRoleSelect(e.target.value)}
                  disabled={isPromptLimitReached}
                  className={`bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full px-3 py-1 text-gray-700 dark:text-gray-200 text-xs shadow-sm hover:shadow focus:ring-2 focus:ring-orange-500 outline-none cursor-pointer w-[100px] sm:w-[110px] truncate ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select Role</option>
                  {roles?.map((role) => (
                    <option key={role} value={role} className="whitespace-normal py-2">
                      {role.slice(0, -1)}
                    </option>
                  ))}
                </select>

                {/* SEND BUTTON */}
                <button
                  onClick={sendMessage}
                  disabled={!selectedRole || !inputValue.trim() || isLoading || isPromptLimitReached}
                  className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition flex-shrink-0
                ${!selectedRole || !inputValue.trim() || isLoading || isPromptLimitReached
                      ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      : "bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                    }`}
                >
                  <Send size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Details Modal */}
      {selectedCandidate && (
        <CandidateDetailsModal
          candidate={selectedCandidate}
          selectedRole={selectedRole}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </div>
  );
};

export default ResumeFilteringAgent;